import { z } from 'zod';
import { logger } from '@/lib/utils/logger';

/**
 * What the generated axios client resolves to, narrowed to what a parse needs: the body,
 * and the request it answered, so a failure can name the endpoint without every call site
 * repeating it.
 */
interface HttpResult {
  data?: unknown;
  config?: { method?: string; url?: string };
}

/** How many issues a message carries before it stops being readable. */
const MAX_REPORTED_ISSUES = 3;

/**
 * Reads a response body against a schema instead of asserting its type.
 *
 * A cast makes a missing field or a null array look like a success, and moves the failure
 * to the first `.map()` or `.length` inside a render, with nothing on screen or in the
 * stack naming the endpoint that sent it. Parsing here fails inside the `queryFn`, so
 * TanStack Query reports it through the same `isError` path a 500 takes, and the message
 * names the endpoint and the field.
 *
 * The message, not the log, carries the diagnostic: `logger` is stripped from production
 * builds, and the consumers that render `error.message` are the ones a user is looking at.
 */
export function parseResponse<T>(schema: z.ZodType<T>, result: HttpResult): T {
  const parsed = schema.safeParse(result.data);
  if (parsed.success) return parsed.data;

  const endpoint = endpointOf(result);
  logger.error('Unreadable API response', { endpoint, issues: parsed.error.issues });
  throw new Error(`${endpoint} returned a body this app cannot read: ${describe(parsed.error)}`);
}

/**
 * A union this app closes but the wire does not.
 *
 * Every one of these is read through a table keyed by the members we know about, so an
 * unrecognised member costs one row its label or its icon. Rejecting it would fail the
 * whole response instead, turning a server-side addition - which the API contract allows
 * within a major version - into a blank screen. The value is carried through as the server
 * sent it; the cast records that the type is narrower than the check.
 */
export const wireUnion = <T extends string>(): z.ZodType<T> =>
  z.string().transform((value) => value as T);

function endpointOf(result: HttpResult): string {
  const method = result.config?.method?.toUpperCase() ?? 'GET';
  // Not copy: a placeholder in a diagnostic naming the request whose body failed to parse.
  // eslint-disable-next-line i18next/no-literal-string
  const url = result.config?.url ?? '<unknown url>';
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  return `${method} ${path}`;
}

function describe(error: z.ZodError): string {
  const reported = error.issues
    .slice(0, MAX_REPORTED_ISSUES)
    .map((issue) => `${issue.path.join('.') || '<root>'} - ${issue.message}`)
    .join('; ');
  const remaining = error.issues.length - MAX_REPORTED_ISSUES;
  return remaining > 0 ? `${reported} (and ${remaining} more)` : reported;
}
